import { AnswerTypeOption, AssessmentSection } from 'types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useFormikContext } from 'formik';
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import QuestionButton from './QuestionButton';
import AssessmentScore from 'components/common/AssessmentScore';
import { convertPointsToScore } from '../assessment-util';

const t = (key) => translate('ui.assessments', key);

function countAnswers(answers, values) {
  const countsPerAnswer = {
    '': 0,
  };
  if (answers) {
    for (const answer of answers) {
      countsPerAnswer[answer.id] = 0;
    }
  }
  for (const id in values as any) {
    const val = (values as any)[id];
    countsPerAnswer[val === undefined ? '' : val] =
      countsPerAnswer[val === undefined ? '' : val] + 1;
  }
  return countsPerAnswer;
}

const QuestionSection = (props: {
  section: AssessmentSection;
  isSubmitting: any;
}): React.ReactElement => {
  const classes = useStyles();
  const { values } = useFormikContext();

  const answers: AnswerTypeOption[] | undefined =
    props.section.assessmentSectionQuestions.length < 1
      ? []
      : props.section.assessmentSectionQuestions[0].assessmentQuestion.answerType.options;
  const countsPerAnswer = countAnswers(answers, values);

  return (
    <Grid container item xs={12} spacing={2}>
      <Grid item xs={8}>
        <div
          className={`${classes.formSection}${props.isSubmitting ? ` ${classes.loading}` : ''}`}
          style={{ flexGrow: 1 }}
        >
          <FormControl component="fieldset">
            <Typography variant="h4" style={{ marginBottom: '20px' }}>
              {props.section.description}
            </Typography>
            {props.section.instructions && (
              <Typography variant="body1" style={{ marginBottom: '20px' }}>
                {props.section.instructions}
              </Typography>
            )}

            <div className={classes.row}>
              {props.section.assessmentSectionQuestions.map((question) => (
                <QuestionButton question={question} key={question.assessmentQuestion.id} />
              ))}
            </div>
          </FormControl>

          <Typography>{t('totals')}</Typography>
          <div className={classes.row}>
            <>
              {answers &&
                answers.map((a) => (
                  <div
                    className={clsx(
                      classes.btnScoring,
                      a.pointValue === 3
                        ? classes.borderYes
                        : a.pointValue === 2 || a.pointValue === 1
                        ? classes.borderSomewhat
                        : classes.borderNo
                    )}
                    key={a.id}
                  >
                    <p>{a.text}</p>
                    <div className={classes.row}>
                      <AssessmentScore score={convertPointsToScore(a?.pointValue)} />
                      <p>{countsPerAnswer[a.id]}</p>
                    </div>
                  </div>
                ))}
              <div className={clsx(classes.btnScoring, classes.borderNotObserved)}>
                <p>{t('notObserved')}</p>
                <div className={classes.row}>
                  <AssessmentScore score={null} />
                  <p>{countsPerAnswer['']}</p>
                </div>
              </div>
            </>
          </div>
        </div>
      </Grid>
      <Grid item xs={4}>
        <div className={classes.formSection}>
          {props.section.thumbnail && (
            <Box
              component="img"
              src={props.section.thumbnail}
              style={{ width: '100%' }}
              className={classes.thumbnail}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default QuestionSection;

const useStyles = makeStyles((theme: Theme) => {
  return {
    row: {
      display: 'flex',
      flexFlow: 'row wrap',
    },
    sectionHeader: {
      color: theme.palette.primary.main,
    },
    btnFilled: {
      borderRadius: '100px',
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(4),
      lineHeight: '1rem',
    },
    formSection: {
      marginTop: theme.spacing(4),

      '& .MuiButton-outlinedPrimary': {
        float: 'right',
        marginBottom: theme.spacing(4),
      },
    },
    footerGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& .MuiButton-root': {
        marginLeft: theme.spacing(2),
      },
    },
    loading: {
      opacity: 0.4,
    },
    centered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
    },
    thumbnail: {
      border: `1px solid ${theme.palette.black.main}`,
      padding: theme.spacing(1),
    },
    btnScoring: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),

      '& p': {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        paddingRight: theme.spacing(2),
        margin: 0,
        whiteSpace: 'nowrap',
      },
    },
    borderYes: {
      border: '2px solid #098e51',
    },
    borderSomewhat: {
      border: '2px solid #6c820c',
    },
    borderNo: {
      border: '2px solid #e86825',
    },
    borderNotObserved: {
      border: '2px solid #0c72b7',
    },
  };
});
